import { useCompanyServices } from 'api/companyApi';
import { useLegalForms } from 'api/defaultApi';
import SelectControl, { SelectControlProps } from 'common/forms/SelectControl';
import ServiceList, { ServiceListProps } from 'components/Form/ServiceList';
import {
  ContractService,
  LegalForm,
  LegalFormListResponse,
  ServicesResponse,
} from 'generated/models';
import withData from 'HOC/withData';
import type { TranslateFn } from 'i18n/types';

export const CompanyServiceSelect = withData<
  ServicesResponse,
  ContractService[],
  object,
  Omit<ServiceListProps, 'options'>
>(ServiceList, {
  namespace: 'company_services',
  propsKey: 'options',
  selector: (response, t, tHtml, isoCode) =>
    response.entries.map((service) => ({
      value: service.code,
      label:
        isoCode === 'cz'
          ? t(`company_services.companyServices.*.${service.nameKey}.name`, service.name)
          : t(`company_services.companyServices.${service.nameKey}`, service.name),
      description:
        isoCode === 'cz'
          ? tHtml(`company_services.companyServices.*.${service.nameKey}.description`, service.name)
          : t(`company_services.companyServices.${service.nameKey}`, service.name),
    })),
  defaultData: [],
  useFetcher: useCompanyServices,
});

export const LegalFormSelectControl = withData<
  LegalFormListResponse,
  LegalForm[],
  object,
  Omit<SelectControlProps, 'options'>
>(SelectControl, {
  namespace: 'legal_forms',
  propsKey: 'options',
  selector: (response, t) =>
    response.entries.map(({ code, name, nameKey }) => ({
      value: code,
      label: t(`legal_forms.legalForm.${nameKey}`, name),
    })),
  defaultData: [],
  useFetcher: useLegalForms,
});

export const getActivityNames = (
  values: string[],
  services: ContractService[] | undefined,
  t: TranslateFn,
) =>
  (services || []).reduce(
    (result, service) => {
      if (values.includes(service.code)) {
        result[service.code] = t(
          `company_services.companyServices.*.${service.nameKey}.name`,
          service.name,
        );
      }
      return result;
    },
    {} as Record<string, string>,
  );
